import type { DisplayObject } from "../runtime";

/** Half-open viewport the camera centres on and clamps against. */
export interface Viewport {
  frameX: number;
  frameY: number;
  frameXHalf: number;
  frameYHalf: number;
  levelX: number;
  levelY: number;
}

/** Who the camera follows. */
export const enum CameraTarget {
  Player = 0,
  Boss = 1,
}

/**
 * The level camera: eases towards its target, applies the look-ahead offset the
 * player's facing produces, adds screen shake and clamps to the level bounds.
 */
export class Camera {
  target = CameraTarget.Player;
  /** Look-ahead the player's movement asks for, eased into `offCurrent`. */
  xOffTarget = 0;
  yOffTarget = 0;

  private xCurrent = 0;
  private yCurrent = 0;
  private xTarget = 0;
  private yTarget = 0;
  private xOffCurrent = 0;
  private yOffCurrent = 0;
  private shakeX = 0;
  private shakeY = 0;
  private shakeAmount = 0;
  private shakeConstant = false;

  moveTo(x: number, y: number): void {
    this.xCurrent = x;
    this.yCurrent = y;
  }

  shake(amount = 0, constant = false): void {
    if (amount > this.shakeAmount) {
      this.shakeAmount = amount;
    }
    this.shakeConstant = constant;
  }

  /** Advances one frame and writes the resulting scroll onto `holder`. */
  update(
    holder: DisplayObject,
    player: { x: number; y: number },
    boss: { xPos: number; yPos: number } | undefined,
    view: Viewport,
  ): void {
    if (this.shakeAmount > 0) {
      if (!this.shakeConstant) {
        --this.shakeAmount;
      }
      this.shakeX =
        Math.round(Math.random() * (this.shakeAmount * 0.6)) -
        this.shakeAmount * 0.3;
      this.shakeY =
        Math.round(Math.random() * (this.shakeAmount * 0.6)) -
        this.shakeAmount * 0.3;
    }
    if (this.target === CameraTarget.Player) {
      this.xTarget = player.x - this.xOffCurrent;
      this.yTarget = player.y - this.yOffCurrent;
    } else if (boss) {
      this.xTarget = boss.xPos - this.xOffCurrent;
      this.yTarget = boss.yPos - this.yOffCurrent;
    }
    this.xOffCurrent += (this.xOffTarget - this.xOffCurrent) * 0.015;
    this.yOffCurrent += (this.yOffTarget - this.yOffCurrent) * 0.015;
    this.xCurrent += (this.xTarget - this.xCurrent) * 0.1;
    this.yCurrent += (this.yTarget - this.yCurrent) * 0.1;
    // the original also eased a zoom factor here, then pinned it to 1 before
    // use, so it never affected the scroll; the dead maths is not carried over
    let x = this.xCurrent * -1 + view.frameXHalf;
    let y = this.yCurrent * -1 + view.frameYHalf;
    if (x > 0) x = 0;
    if (y > 0) y = 0;
    if (x < view.frameX - view.levelX) x = view.frameX - view.levelX;
    if (y < view.frameY - view.levelY) y = view.frameY - view.levelY;
    holder.x = x + this.shakeX;
    holder.y = y + this.shakeY;
  }

  reset(): void {
    this.xCurrent = 0;
    this.yCurrent = 0;
    this.xTarget = 0;
    this.yTarget = 0;
    this.xOffCurrent = 0;
    this.yOffCurrent = 0;
    this.xOffTarget = 0;
    this.yOffTarget = 0;
    this.target = CameraTarget.Player;
    this.shakeX = 0;
    this.shakeY = 0;
    this.shakeAmount = 0;
    this.shakeConstant = false;
  }
}
